import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Clock } from '@phosphor-icons/react';
import { formatHoursHM } from '@/utils/formatHours';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function EmployeeAttendance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {
    try {
      const { data } = await axios.get(`${API}/attendance/history`, { withCredentials: true });
      setAttendance(data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    // Pin to Asia/Riyadh so the employee always sees the canonical Saudi
    // clock regardless of phone/device timezone. 24-hour format.
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Riyadh',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: 'bg-green-100 text-green-700 border-green-200',
      absent: 'bg-red-100 text-red-700 border-red-200',
      late: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return variants[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="employee-attendance-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6]">{t('my_attendance')}</h1>

        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <Clock size={22} weight="duotone" className="text-[#655dc6]" />
              {t('attendance_history')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-thin" data-mobile-hint="scroll-x">
                <Table data-testid="attendance-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      <TableHead className="text-start">{t('date')}</TableHead>
                      <TableHead className="text-start">{t('clock_in')}</TableHead>
                      <TableHead className="text-start">{t('clock_out')}</TableHead>
                      <TableHead className="text-start">{t('work_hours')}</TableHead>
                      <TableHead className="text-start">{t('status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendance.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-[#655dc6]/70">
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      attendance.map((record, index) => (
                        <TableRow key={index} className="border-[#C4BFF0]">
                          <TableCell className="font-medium text-[#655dc6]">{record.date}</TableCell>
                          <TableCell className="text-[#655dc6]/70">{formatTime(record.clock_in)}</TableCell>
                          <TableCell className="text-[#655dc6]/70">{formatTime(record.clock_out)}</TableCell>
                          <TableCell className="text-[#655dc6]/70">
                            {(() => {
                              // Overtime is HIDDEN from the employee — it
                              // appears exclusively on the published
                              // payslip. Show raw worked hours only.
                              const raw = record.work_hours_raw != null ? record.work_hours_raw : record.work_hours;
                              if (raw == null) return '-';
                              return <div className="font-medium">{formatHoursHM(raw)}</div>;
                            })()}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(record.status)}>
                              {record.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
